import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Pencil, Trash2, ExternalLink, Lock } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";

export default function PlatformPagesManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ slug: "", title: "" });
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform-pages");
      setPages(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/admin/platform-pages", {
        slug: form.slug.trim().toLowerCase(),
        title: form.title.trim() || form.slug,
        blocks: [],
        published: true,
      });
      setCreating(false);
      setForm({ slug: "", title: "" });
      await load();
      navigate(`/admin/pages/${data.id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Konnte nicht angelegt werden");
    }
  };

  const remove = async (p) => {
    if (p.system) return;
    if (!confirm(`Seite „${p.title}" wirklich löschen?`)) return;
    await api.delete(`/admin/platform-pages/${p.id}`);
    load();
  };

  return (
    <AdminLayout>
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-4xl">Plattform-Seiten</h1>
          <button data-testid="new-platform-page-btn" onClick={() => setCreating(true)} className="mv-btn">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Neue Seite
          </button>
        </div>
        <div className="mb-6">
          <p className="mv-label mb-2">Editierbare Plattform-Seiten</p>
          <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>
            Diese Seiten erscheinen unter <code className="font-mono text-xs">studiomv.de/&lt;slug&gt;</code> und können
            mit dem Drag&amp;Drop-Builder beliebig gestaltet werden. <strong>Impressum</strong>, <strong>Datenschutz</strong>{" "}
            und <strong>AGB</strong> sind als System-Seiten markiert und können nicht gelöscht werden.
          </p>
        </div>

        {creating && (
          <form onSubmit={create} className="mv-card p-5 mb-6" data-testid="new-page-form">
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs mb-1">Titel</label>
                <input data-testid="new-page-title" autoFocus className="mv-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z. B. Über uns" />
              </div>
              <div>
                <label className="block text-xs mb-1">Slug (URL)</label>
                <input data-testid="new-page-slug" className="mv-input font-mono text-sm" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="ueber-uns" />
              </div>
            </div>
            {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => { setCreating(false); setErr(""); }} className="mv-btn-ghost text-sm">Abbrechen</button>
              <button data-testid="create-page-submit" type="submit" disabled={!form.slug} className="mv-btn">
                <Plus className="w-4 h-4" /> Anlegen
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>Lade…</p>
        ) : (
          <div className="grid gap-3">
            {pages.map((p) => (
              <div key={p.id} data-testid={`platform-page-row-${p.slug}`} className="mv-card p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg">{p.title}</h3>
                    {p.system && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}><Lock className="w-3 h-3 inline mr-1" strokeWidth={1.5} />System</span>}
                    {!p.published && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded text-red-700 bg-red-50">Entwurf</span>}
                  </div>
                  <p className="text-xs font-mono mt-1" style={{ color: "var(--mv-text-muted)" }}>/{p.slug}</p>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="mv-btn-ghost text-sm" data-testid={`view-${p.slug}`}>
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Ansehen
                  </a>
                  <Link to={`/admin/pages/${p.id}`} className="mv-btn-ghost text-sm" data-testid={`edit-${p.slug}`}>
                    <Pencil className="w-4 h-4" strokeWidth={1.5} /> Bearbeiten
                  </Link>
                  {!p.system && (
                    <button onClick={() => remove(p)} className="mv-btn-ghost text-sm text-red-600" data-testid={`del-${p.slug}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </AdminLayout>
  );
}
